<script setup>
import { computed, onMounted } from 'vue';
import HighlightsSection from '@/components/configuratorSections/HighlightsSection';
import PowertrainSectionV2 from '@/components/configuratorSections/PowertrainSectionV2';
import ExteriorSectionV2 from '@/components/configuratorSections/ExteriorSectionV2';
import InteriorSectionV2 from '@/components/configuratorSections/InteriorSectionV2';
import SafetySectionGroupV2 from '@/components/configuratorSections/SafetySectionGroupV2';
import ConnectedServicesSection from '@/components/configuratorSections/ConnectedServicesSection';
import RepairMaintenanceSection from '@/components/configuratorSections/RepairMaintenanceSection';

import { useMainStore } from '@/store/index.js';

const store = useMainStore();

const staticImagesBaseUrl = process.env.VUE_APP_DS_STATIC_IMAGES_BASE_URL;

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  offerTemplate: {
    type: String,
    default: 'JP_STANDARD_RIGID',
    required: true,
  },
  specType: {
    type: String,
    default: '',
    required: true,
  },
  isFrameAgreement: {
    type: Boolean,
    default: false,
    required: true,
  },
});

const emit = defineEmits(['select']);

onMounted(() => {
  store.sections = [
    { id: 'highlights', name: 'highlightsHeaderTitle' },
    { id: 'powertrain', name: 'drivelineHeaderTitle' },
    { id: 'interior', name: 'interiorHeaderTitle' },
    { id: 'safety', name: 'safetyHeaderTitle' },
    ...((options.value.chassis || options.value.bodyBuild) ? [{ id: 'exterior', name: 'exteriorHeaderTitle' }] : []),
    { id: 'connectedServices', name: 'connectedServicesHeaderTitle' },
    { id: 'repairMaintenance', name: 'repairMaintenanceHeaderTitle' },
  ];
});

const options = computed(() => {
  return store.options;
});

const images = computed(() => {
  return store.images;
});

const interiorImages = computed(() => {
  return { interior: images.value.interior, dashboard: images.value.dashboard };
});

const isBev = computed(() => {
  return props.offerTemplate.endsWith('_BEV');
});

const isTractor = computed(() => {
  return props.offerTemplate.endsWith('STANDARD_TRACTOR');
});

const imageChassisSection = computed(() => {
  return (store.sectionMetadata?.chassis?.descriptionImage) ? `${staticImagesBaseUrl}/${store.sectionMetadata?.chassis?.descriptionImage}` : null;
});

const chassisHighlights = computed(() => {
  return store.misc?.chassisHighlights;
});

const getExteriorSectionByType = (type) => {
  if (options.value?.chassis?.some(item => item.id === type)) return 'chassis';
  return 'bodyBuild';
};

const handleExteriorSelect = (selection) => {
  emit('select', selection, getExteriorSectionByType(selection?.type));
};
</script>
<template>
  <HighlightsSection
    v-if="options.highlights"
    :items="options.highlights"
  />
  <PowertrainSectionV2
    :items="options.driveline"
    @select="emit('select', $event, 'driveline')"
  />
  <InteriorSectionV2
    v-if="options.interior"
    :items="options.interior"
    :images="interiorImages"
    @select="emit('select', $event, 'interior')"
  />
  <SafetySectionGroupV2
    :items="options.safety"
    @select="emit('select', $event, 'safety')"
  />
  <ExteriorSectionV2
    v-if="options.chassis || options.bodyBuild"
    :items="{ chassis: options.chassis, bodyBuild: options.bodyBuild }"
    :images="{ side: imageChassisSection, isSide: images?.side }"
    :offer-template="offerTemplate"
    :spec-type="specType"
    :is-frame-agreement="isFrameAgreement"
    :is-bev="isBev"
    :is-tractor="isTractor"
    :chassis-description-key="chassisHighlights"
    :show-dimensions-subheader="false"
    @select="handleExteriorSelect"
  />
  <ConnectedServicesSection
    :items="options.connectedServices"
    @select="emit('select', $event, 'connectedServices')"
  />
  <RepairMaintenanceSection
    :items="options.repairMaintenance"
    @select="emit('select', $event, 'repairMaintenance')"
  />
</template>


<style scoped>
.chasis-section >>> .configurator-section__image > img {
  max-width: 70%;
}

@media only screen and (max-width: 1294px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 80%;
  }
}

@media only screen and (max-width: 1000px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 90%;
  }
}

@media only screen and (max-width: 600px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 120%;
  }
}
</style>
